import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import moment from 'moment';
import DriverService from '../../services/driver/driver-service';
import { companyNameReplace, copyText, regionDateAndTime } from '../../utils';
import { Common } from '../../utils/constants';
import ability, { Can } from '../../ability';
import images from '../../assets/images';
import Table from '../../components/table/Table';
import { IPastLetterDetails, IRunHistoryDetails } from '../../models/driver-debt';
import { IDriverDebtContentProps } from '../../types/propTypes';
import { useDriverDebtContent } from './hooks/DriverDebtContentHook';
import RegenerateLetterModal from './RegenerateLetterModal';

const DriverDebtContent = (props: IDriverDebtContentProps) => {
    const {
        s1Days,
        s2Days,
        s1Letters,
        s2Letters,
        s1Letter,
        s2Letter,
        status,
        runHistoryList,
        totalCount,
        pastLettersList,
        plTotalCount,
        regenerateLetterModal,
        setS1Letter,
        setS2Letter,
        startGeneration,
        getFileName,
        handlePageChange,
        handlePastLettersPageChange,
        handleSorting,
        handlePastLettersSorting,
        manageRegenerateLetterModal,
        refreshPastLettersList,
    } = useDriverDebtContent(props);
    const { t } = useTranslation();

    const driverService = new DriverService();

    const filterState = props.filterState;
    const pastLettersFilterState = props.pastLettersFilterState;

    const header = [
        {
            label: t('driverDebt.columns.run'),
            id: 'id',
            sortable: false,
            isVisible: true,
            cell: (row: IRunHistoryDetails) => {
                return (
                    <span className="d-block text-nowrap-normal" title={row.id.toString()}>
                        {row.id}
                    </span>
                );
            },
        },
        {
            label: t('driverDebt.columns.driverIDs'),
            id: 'driver_ids',
            sortable: false,
            isVisible: true,
            style: { maxWidth: 250 },
            cell: (row: IRunHistoryDetails) => {
                return (
                    <span
                        className="d-block text-truncate"
                        title={t<string>('driverDebt.label.copyDriverIds')}
                        style={{ cursor: 'pointer' }}
                        onClick={() => copyText(row.driver_ids)}
                    >
                        {row.driver_ids}
                    </span>
                );
            },
        },
        {
            label: t('driverDebt.columns.runTimestamp'),
            id: 'creation_date_time',
            sortable: true,
            isVisible: true,
            cell: (row: IRunHistoryDetails) => {
                if (row.creation_date_time) {
                    return moment(row.creation_date_time).format(regionDateAndTime()?.GENERAL_DATE_TIME_FORMAT);
                }
                return null;
            },
        },
        {
            label: t('driverDebt.columns.strikeType'),
            id: 'chase_report_type',
            sortable: true,
            isVisible: true,
            cell: (row: IRunHistoryDetails) => {
                return (
                    <span className="d-block text-nowrap-normal" title={row.chase_report_type?.code}>
                        {row.chase_report_type?.code}
                    </span>
                );
            },
        },
        {
            label: t('driverDebt.columns.initiatedByUser'),
            id: 'initiated_by',
            sortable: true,
            isVisible: true,
            cell: (row: IRunHistoryDetails) => {
                return (
                    <span
                        className="d-block text-nowrap-normal"
                        title={row.chase_report_generation_history_data[0]?.initiated_by}
                    >
                        {row.chase_report_generation_history_data[0]?.initiated_by}
                    </span>
                );
            },
        },
        {
            label: t('driverDebt.columns.action'),
            id: 'action',
            sortable: false,
            isVisible: ability.can(Common.Actions.REGENERATE_DEBT_LETTER, Common.Modules.DRIVERS.DRIVER_DEBT),
            style: { width: 80 },
            className: 'text-center',
            cell: (row: IRunHistoryDetails) => {
                return (
                    <a
                        className="icon pointer"
                        title={t<string>('driverDebt.button.regenerateLetters')}
                        onClick={() => manageRegenerateLetterModal(true, row)}
                    >
                        <img
                            src={images.replaceSVG}
                            alt={t<string>('driverDebt.button.regenerateLetters')}
                            className="action-icon-img"
                        />
                    </a>
                );
            },
        },
    ];

    const pastLettersHeader = [
        {
            label: t('driverDebt.columns.relatedRun'),
            id: 'chase_report_data_id',
            sortable: true,
            isVisible: true,
            cell: (row: IPastLetterDetails) => {
                return (
                    <span className="d-block text-nowrap-normal" title={row.chase_report_data_id}>
                        {row.chase_report_data_id}
                    </span>
                );
            },
        },
        {
            label: t('driverDebt.columns.date'),
            id: 'creation_date_time',
            sortable: true,
            isVisible: true,
            cell: (row: IPastLetterDetails) => {
                if (row.creation_date_time) {
                    return moment(row.creation_date_time).format(regionDateAndTime()?.GENERAL_DATE_TIME_FORMAT);
                }
                return null;
            },
        },
        {
            label: t('driverDebt.columns.file'),
            id: 'file_name',
            sortable: false,
            isVisible: true,
            cell: (row: IPastLetterDetails) => {
                if (row.file_name) {
                    return (
                        <span
                            className={
                                ability.can(Common.Actions.DOWNLOAD_DEBT_LETTER, Common.Modules.DRIVERS.DRIVER_DEBT) &&
                                (!row.is_file_deleted_from_s3 || row.is_file_deleted_from_s3 === null)
                                    ? 'd-block text-nowrap-normal click-here'
                                    : 'd-block text-nowrap-normal'
                            }
                            title={getFileName(row.file_name)}
                            onClick={() => {
                                if (
                                    ability.can(
                                        Common.Actions.DOWNLOAD_DEBT_LETTER,
                                        Common.Modules.DRIVERS.DRIVER_DEBT,
                                    ) &&
                                    (!row.is_file_deleted_from_s3 || row.is_file_deleted_from_s3 === null)
                                )
                                    driverService.downloadDriverDebtChaseLetter(row.file_name);
                            }}
                        >
                            {getFileName(row.file_name)}
                        </span>
                    );
                }
                return (
                    <span
                        className="d-block text-nowrap-normal"
                        title={t<string>('driverDebt.label.reportGenerationInProgress')}
                    >
                        {t('driverDebt.label.reportGenerationInProgress')}
                    </span>
                );
            },
        },
        {
            label: t('driverDebt.columns.templateUsed'),
            id: 'debtor_letter_name',
            sortable: true,
            isVisible: true,
            cell: (row: IPastLetterDetails) => {
                return (
                    <span className="d-block text-nowrap-normal" title={row.debtor_letter_name}>
                        {row.debtor_letter_name}
                    </span>
                );
            },
        },
        {
            label: t('driverDebt.columns.generatedBy'),
            id: 'initiated_by',
            sortable: true,
            isVisible: true,
            cell: (row: IPastLetterDetails) => {
                return (
                    <span className="d-block text-nowrap-normal" title={row.initiated_by}>
                        {row.initiated_by}
                    </span>
                );
            },
        },
    ];

    return (
        <>
            <Helmet>
                <title>{companyNameReplace(t('driverDebt.title'))}</title>
            </Helmet>
            <div className="title row align-items-content justify-content-between pb-3">
                <div className="col-auto col-sm text-truncate pr-0">
                    <h1 className="text-truncate">{t('driverDebt.header')}</h1>
                </div>
            </div>
            <div className="card-outer driver-debt-page">
                <Can I={Common.Actions.GENERATE_CHASE_REPORT} a={Common.Modules.DRIVERS.DRIVER_DEBT}>
                    <div className="pt-3 px-3">
                        {+s1Days !== 0 && (
                            <div className="row">
                                <div className="form-group col-md-6 col-lg-4 col-xl-3">
                                    <label>{t('driverDebt.button.strikeOneLetter')}</label>
                                    <Select
                                        name="status"
                                        value={s1Letter}
                                        placeholder={t('driverDebt.placeholder.strikeOneLetter')}
                                        isMulti={false}
                                        options={s1Letters}
                                        isClearable={false}
                                        onChange={(value) => setS1Letter(value)}
                                        noOptionsMessage={() => t('noOptions')}
                                        styles={{
                                            input: (styles) => ({
                                                ...styles,
                                                borderRadius: '4px',
                                                borderColor: '#dadada !important',
                                                outline: 'none !important',
                                                boxShadow: 'none !important',
                                            }),
                                        }}
                                    />
                                </div>
                                <div className="form-group col-md-auto mt-lg-23p">
                                    <button
                                        type="button"
                                        className="btn btn-primary m-w-170p"
                                        onClick={() => startGeneration(Common.LookupCode.STRIKE_ONE)}
                                        disabled={
                                            status.STRIKE_ONE?.status === Common.LookupCode.IN_PROGRESS ||
                                            !s1Letter ||
                                            (status.STRIKE_ONE?.status === Common.LookupCode.COMPLETED &&
                                                status.STRIKE_ONE.last_generated_report.creation_date_time &&
                                                moment(
                                                    status.STRIKE_ONE.last_generated_report.creation_date_time,
                                                ).isSame(moment(), 'day'))
                                        }
                                        title={t<string>('driverDebt.button.generateStrikeOneLetters')}
                                    >
                                        <span>{t('driverDebt.button.generateStrikeOneLetters')}</span>
                                    </button>
                                    <span className="pl-3">
                                        {status.STRIKE_ONE?.status === Common.LookupCode.IN_PROGRESS &&
                                            t('driverDebt.label.inProgress')}
                                    </span>
                                </div>
                            </div>
                        )}
                        {+s2Days !== 0 && (
                            <div className="row">
                                <div className="form-group col-md-6 col-lg-4 col-xl-3">
                                    <label>{t('driverDebt.button.strikeTwoLetter')}</label>
                                    <Select
                                        name="status"
                                        value={s2Letter}
                                        isMulti={false}
                                        options={s2Letters}
                                        onChange={(value) => setS2Letter(value)}
                                        isClearable={false}
                                        noOptionsMessage={() => t('noOptions')}
                                        styles={{
                                            input: (styles) => ({
                                                ...styles,
                                                borderRadius: '4px',
                                                borderColor: '#dadada !important',
                                                outline: 'none !important',
                                                boxShadow: 'none !important',
                                            }),
                                        }}
                                        placeholder={t('driverDebt.placeholder.strikeTwoLetter')}
                                    />
                                </div>
                                <div className="form-group col-md-auto mt-lg-23p">
                                    <button
                                        type="button"
                                        className="btn btn-primary m-w-170p"
                                        onClick={() => startGeneration(Common.LookupCode.STRIKE_TWO)}
                                        disabled={
                                            status.STRIKE_TWO?.status === Common.LookupCode.IN_PROGRESS ||
                                            !s2Letter ||
                                            (status.STRIKE_TWO?.status === Common.LookupCode.COMPLETED &&
                                                status.STRIKE_TWO.last_generated_report.creation_date_time &&
                                                moment(
                                                    status.STRIKE_TWO.last_generated_report.creation_date_time,
                                                ).isSame(moment(), 'day'))
                                        }
                                        title={t<string>('driverDebt.button.generateStrikeTwoLetters')}
                                    >
                                        <span>{t('driverDebt.button.generateStrikeTwoLetters')}</span>
                                    </button>
                                    <span className="pl-3">
                                        {status.STRIKE_TWO?.status === Common.LookupCode.IN_PROGRESS &&
                                            t('driverDebt.label.inProgress')}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </Can>
                <div className="px-2 pt-2">
                    <span className="debt-grid-header">{t('driverDebt.button.runHistory')}</span>
                </div>
                <Table
                    columns={header}
                    data={runHistoryList}
                    rowsPerPage={filterState.rowsPerPage}
                    currentPage={filterState.currentPage}
                    totalCount={totalCount}
                    sortColumn={filterState.sortColumn}
                    sortOrder={filterState.sortOrder}
                    handlePageChange={handlePageChange}
                    handleSorting={handleSorting}
                    t={t}
                />
                <div className="px-2 mt-2 d-flex justify-content-between">
                    <div className="d-flex align-items-center">
                        <span className="debt-grid-header">{t('driverDebt.button.pastLetters')}</span>
                    </div>
                    <div>
                        <button
                            title={t<string>('driverDebt.button.refreshPastLetters')}
                            type="button"
                            id="refreshPastLetters"
                            className="btn icon-btn pr-0 btn-outline-primary mr-2"
                            onClick={() => refreshPastLettersList()}
                        >
                            <img
                                className="icon sync-icon"
                                src={images.synchronizeSVG}
                                alt={t<string>('driverDebt.button.refreshPastLetters')}
                            />
                        </button>
                    </div>
                </div>
                <Table
                    columns={pastLettersHeader}
                    data={pastLettersList}
                    rowsPerPage={pastLettersFilterState.rowsPerPage}
                    currentPage={pastLettersFilterState.currentPage}
                    totalCount={plTotalCount}
                    sortColumn={pastLettersFilterState.sortColumn}
                    sortOrder={pastLettersFilterState.sortOrder}
                    handlePageChange={handlePastLettersPageChange}
                    handleSorting={handlePastLettersSorting}
                    t={t}
                />
            </div>
            {regenerateLetterModal.showModal && (
                <RegenerateLetterModal
                    showModal={regenerateLetterModal.showModal}
                    closeModal={() => manageRegenerateLetterModal(false)}
                    s1Letters={s1Letters}
                    s2Letters={s2Letters}
                    runHistoryDetails={regenerateLetterModal.runHistoryDetails}
                    refreshPastLettersList={refreshPastLettersList}
                />
            )}
        </>
    );
};
export default DriverDebtContent;
